import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { useHistory } from 'react-router-dom';
import { type ErrorOrNull } from '../../../typings/utils';
import {
  completeConnection,
  type JiraResource,
  updateCompleteConnection
} from '../../common/utils/integrations-client/jira/on-prem/connection';
import { useIntegrations } from '../hooks/useIntegrations';
import { useAuthContext } from '../../common/contexts/auth';
import {
  InterWindowMessageType,
  type InterWindowMessage
} from '../../common/types';
import CommonConnected from './CommonConnected';
import {
  Button,
  ColumnHeader,
  PanelContent,
  Select,
  type SelectOption
} from '@deque/cauldron-react';
import styles from './JiraDirectConnected.css';

enum CONNECTION_STAGES {
  GET_ACCESS_TOKEN,
  SELECT_SITE,
  COMPLETED
}

let selectedSiteID: string | null = null;

function JiraDirectConnected() {
  const { t } = useTranslation();

  const [connectionsStage, setConnectionsStage] = useState<CONNECTION_STAGES>(
    CONNECTION_STAGES.GET_ACCESS_TOKEN
  );

  const [connectionsError, setConnectionsError] = useState<ErrorOrNull>(null);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [connectionDenied, setConnectionDenied] = useState(false);

  const [siteSelectOptions, setSiteSelectOptions] = useState<SelectOption[]>(
    []
  );
  const [jiraResources, setJiraResources] = useState<JiraResource[]>([]);

  const history = useHistory();
  const {
    loading: integrationsLoading,
    error: integrationsError,
    activeEnterprise
  } = useIntegrations();

  const { user: authUser } = useAuthContext();
  useEffect(() => {
    const { code, connection_id, error, error_description } = queryString.parse(
      history.location.search,
      {
        // This is the default, but we're being explicit here.
        decode: true
      }
    );

    async function setSelectedSiteId() {
      if (activeEnterprise && authUser && selectedSiteID && !connectionDenied) {
        try {
          // find url for this site
          const selectedResource = jiraResources.find(jiraResource => {
            return jiraResource.id === selectedSiteID;
          });

          if (selectedResource) {
            // Set the site we want to configure for this connection
            const updateConnection = await updateCompleteConnection({
              enterpriseId: activeEnterprise.id,
              connectionId: connection_id as string,
              token: authUser.token,
              body: {
                cloud_id: selectedResource.id,
                base_url: selectedResource.url
              }
            });

            // Update main window, so it can refresh list of connections
            const messagePayload: InterWindowMessage = {
              message_type:
                InterWindowMessageType.REFRESH_INTEGRATION_CONNECTION,
              data: {
                connection_id: updateConnection.connection.id,
                product_slug: updateConnection.connection.product_slug
              }
            };
            window.opener.postMessage(messagePayload, location.origin);
          }
        } catch (exception) {
          // eslint-disable-next-line no-console
          console.error('Failed to update connection', exception);

          const errorMessage = t('Failed to set selected site id.');
          setConnectionsError(new Error(errorMessage));
        } finally {
          setConnectionsLoading(false);
        }
      }
    }

    async function getAccessToken() {
      if (activeEnterprise && authUser) {
        try {
          setConnectionsLoading(true);

          // Complete the connection, which will store access and refresh tokens
          // This will return a list of sites that user has access to
          const { resources } = await completeConnection({
            enterpriseId: activeEnterprise.id,
            connectionId: connection_id as string,
            token: authUser.token,
            body: {
              oauth_code: code as string
            }
          });

          // Store resources - we will need it later to get url
          setJiraResources(resources);

          // Convert resources to select options to show in UI
          const preparedSiteSelectOptions: SelectOption[] = resources.map(
            resource => {
              return {
                key: resource.id,
                label: resource.name,
                value: resource.id
              };
            }
          );
          setSiteSelectOptions(preparedSiteSelectOptions);

          // Default to the first site, to reflect the default value that will be in the drop down selector
          selectedSiteID = preparedSiteSelectOptions[0].key;

          // If we only have one site, then do not ask user to pick which site to access
          if (resources.length === 1) {
            setConnectionsStage(CONNECTION_STAGES.COMPLETED);
          } else {
            setConnectionsStage(CONNECTION_STAGES.SELECT_SITE);
          }
        } catch (exception) {
          // eslint-disable-next-line no-console
          console.error('Failed to complete connection', exception);

          const errorMessage = t('Failed to register new connection.');
          setConnectionsError(new Error(errorMessage));
        } finally {
          setConnectionsLoading(false);
        }
      }
    }
    if (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Failed to create direct Jira conection',
        error_description
      );

      setConnectionDenied(true);
      setConnectionsLoading(false);
    } else {
      switch (connectionsStage) {
        case CONNECTION_STAGES.GET_ACCESS_TOKEN:
          getAccessToken();
          break;
        case CONNECTION_STAGES.COMPLETED:
          setSelectedSiteId();
          break;
      }
    }
  }, [activeEnterprise, authUser, connectionsStage]);

  switch (connectionsStage) {
    case CONNECTION_STAGES.GET_ACCESS_TOKEN:
      return (
        <CommonConnected
          connectionsLoading={connectionsLoading}
          integrationsLoading={integrationsLoading}
          connectionsError={connectionsError}
          integrationsError={integrationsError}
          connectionDenied={connectionDenied}
        />
      );
    case CONNECTION_STAGES.SELECT_SITE:
      return (
        <>
          <ColumnHeader>
            <h2>{t('Jira Connection')}</h2>
          </ColumnHeader>
          <div className={styles.siteSelectorContent}>
            <PanelContent>
              <Select
                label={t('Select site you want to use')}
                options={siteSelectOptions}
                onChange={e => {
                  selectedSiteID = e.target.value;
                }}
              />
            </PanelContent>
            <PanelContent>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setConnectionDenied(true);

                  setConnectionsStage(CONNECTION_STAGES.COMPLETED);
                }}
              >
                {t('Cancel')}
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  setConnectionsStage(CONNECTION_STAGES.COMPLETED);
                }}
              >
                {t('Select and Save')}
              </Button>
            </PanelContent>
          </div>
        </>
      );
    case CONNECTION_STAGES.COMPLETED:
      return (
        <CommonConnected
          connectionsLoading={connectionsLoading}
          integrationsLoading={integrationsLoading}
          connectionsError={connectionsError}
          integrationsError={integrationsError}
          connectionDenied={connectionDenied}
        />
      );
  }
}

export default JiraDirectConnected;
